import { apiGet, apiPost, apiDelete } from '@/utils/api';
import type {
  BackupStatistics,
  PaginatedBackups,
  BackupJob,
  BackupSchedule,
  CreateBackupDto,
  CreateScheduleDto,
  RestoreBackupDto,
  BackupRestoreHistory,
  BackupStatus,
} from '@/types/backup.types';

const BASE_URL = '/backup';

export const backupService = {
  // Statistics
  getStatistics: async (): Promise<BackupStatistics> => {
    return apiGet(`${BASE_URL}/statistics`);
  },

  // Backup Jobs
  listBackups: async (
    page = 1,
    limit = 20,
    status?: BackupStatus
  ): Promise<PaginatedBackups> => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (status) params.set('status', status);

    return apiGet(`${BASE_URL}/jobs?${params.toString()}`);
  },

  getBackup: async (id: string): Promise<BackupJob & { restoreHistory: BackupRestoreHistory[] }> => {
    return apiGet(`${BASE_URL}/jobs/${id}`);
  },

  createBackup: async (dto: CreateBackupDto): Promise<BackupJob> => {
    return apiPost(`${BASE_URL}/jobs`, dto);
  },

  downloadBackup: async (id: string): Promise<void> => {
    const response = await fetch(`/api${BASE_URL}/jobs/${id}/download`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('nexedu_access_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download backup');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
    const filename = filenameMatch ? filenameMatch[1] : `backup-${id}.sql.gz`;

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  deleteBackup: async (id: string): Promise<{ message: string }> => {
    return apiDelete(`${BASE_URL}/jobs/${id}`);
  },

  // Restore
  restoreBackup: async (dto: RestoreBackupDto): Promise<BackupRestoreHistory> => {
    return apiPost(`${BASE_URL}/restore`, dto);
  },

  // Schedules
  listSchedules: async (): Promise<BackupSchedule[]> => {
    return apiGet(`${BASE_URL}/schedules`);
  },

  createSchedule: async (dto: CreateScheduleDto): Promise<BackupSchedule> => {
    return apiPost(`${BASE_URL}/schedules`, dto);
  },

  deleteSchedule: async (id: string): Promise<{ message: string }> => {
    return apiDelete(`${BASE_URL}/schedules/${id}`);
  },

  // Retention
  applyRetention: async (): Promise<{ message: string }> => {
    return apiPost(`${BASE_URL}/retention/apply`, {});
  },
};
